import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Pagination
} from '@mui/material';
import { invoiceAPI, vendorAPI, categoryAPI } from '../api/api';
import { useNavigate } from 'react-router-dom';
import EditInvoiceDialog from '../components/EditInvoiceDialog';
import PdfViewerWithDetails from '../components/PdfViewerWithDetails';
import { Receipt as ReceiptIcon, Add as AddIcon, Search as SearchIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Filter state
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  // Sorting state
  const [sortBy, setSortBy] = useState('created_at'); // Default sort by creation date
  const [sortOrder, setSortOrder] = useState('desc'); // Default sort order

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50; // Fixed to 50 as requested

  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // Function to fetch invoices with pagination
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchInvoices = async (page = 1) => {
    try {
      // Calculate skip based on page (page 1 = skip 0, page 2 = skip 50, etc.)
      const skip = (page - 1) * itemsPerPage;

      // Get invoices with filters and sorting
      const allResponse = await invoiceAPI.getInvoices(skip, itemsPerPage, selectedVendorId || null, selectedYear || null, sortBy, sortOrder);
      setInvoices(allResponse.data);

      // Get total count for pagination (with filters but without sorting for consistency)
      const totalResponse = await invoiceAPI.getInvoices(0, 10000, selectedVendorId || null, selectedYear || null); // Large limit to get all
      setTotalItems(totalResponse.data.length);
    } catch (err) {
      // Handle different error response formats
      let errorMessage = 'Failed to load invoices';
      if (err.response?.data?.detail) {
        // Handle FastAPI validation errors which can be an array of objects
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg || JSON.stringify(error)).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }

      setError(errorMessage);
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendors, categories, and available years
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOptions = async () => {
    try {
      const [vendorsResponse, categoriesResponse, yearsResponse] = await Promise.all([
        vendorAPI.getVendors(),
        categoryAPI.getCategories(),
        invoiceAPI.getAvailableYears()
      ]);
      setVendors(vendorsResponse.data);
      setCategories(categoriesResponse.data);
      setAvailableYears(yearsResponse.data);
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  };

  useEffect(() => {
    // Initial fetch with pagination, filters, and sorting
    fetchInvoices(currentPage);
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedVendorId, selectedYear, sortBy, sortOrder]); // Re-fetch when currentPage, filter, or sort parameters change

  // Refresh data when component comes into view (e.g., after navigating back from upload)
  useEffect(() => {
    const handleFocus = () => {
      fetchInvoices(currentPage);
      fetchOptions();
    };

    // Add event listener for focus (when user returns to page)
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, sortOrder, fetchInvoices, fetchOptions]);

  const handleClick = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
    handleClose();
  };

  const handleEditClick = () => {
    setShowEditDialog(true);
    handleClose();
  };

  const handleViewDetailsClick = () => {
    setShowPdfViewer(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInvoice) return;

    setDeleting(true);
    setDeleteError('');

    try {
      await invoiceAPI.deleteInvoice(selectedInvoice.id);

      // Clear any potential cached invoice data from local storage
      const cachedInvoiceKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('invoice_') || key.startsWith('invoices_')
      );
      cachedInvoiceKeys.forEach(key => localStorage.removeItem(key));

      // Refresh the invoices list after deletion
      fetchInvoices(currentPage);

      // Close the dialog and reset selected invoice
      setShowDeleteDialog(false);
      setSelectedInvoice(null);
    } catch (err) {
      // Handle different error response formats
      let errorMessage = 'Failed to delete invoice';
      if (err.response?.data?.detail) {
        // Handle FastAPI validation errors which can be an array of objects
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.response?.data && Array.isArray(err.response.data)) {
        errorMessage = err.response.data.map(error => error.msg || JSON.stringify(error)).join(', ');
      } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }

      setDeleteError(errorMessage);
      console.error('Error deleting invoice:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setSelectedInvoice(null);
  };

  const handleEditSave = () => {
    // Refresh the invoices list after editing
    fetchInvoices(currentPage);

    // Also refresh vendors and categories to get updated standard names
    const fetchOptions = async () => {
      try {
        const [vendorsResponse, categoriesResponse] = await Promise.all([
          vendorAPI.getVendors(),
          categoryAPI.getCategories()
        ]);
        setVendors(vendorsResponse.data);
        setCategories(categoriesResponse.data);
      } catch (err) {
        console.error('Error fetching options after edit:', err);
      }
    };
    fetchOptions();
  };

  const handleEditError = (errorMessage) => {
    setError(errorMessage);
  };

  // Function to handle sorting
  const handleSort = (field) => {
    // If clicking the same field as current sort, toggle the sort order
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } 
    // If clicking a different field, sort by that field in descending order by default
    else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Unpaid':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Helper function to get the display name based on standard_name
  const getVendorDisplayName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? (vendor.standard_name || vendor.name) : 'N/A';
  };

  const getCategoryDisplayName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? (category.standard_name || category.name) : 'N/A';
  };

  // Filter invoices based on search term (now applied to current page)
  const filteredInvoices = invoices.filter(invoice =>
    invoice.id.toString().includes(searchTerm) ||
    (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
    getVendorDisplayName(invoice.vendor_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCategoryDisplayName(invoice.category_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.payment_status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination data
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mt: 4,
            textAlign: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <Typography color="error" variant="h6">
            {typeof error === 'string'
              ? error
              : Array.isArray(error)
                ? error.map((err, idx) => <div key={idx}>{err.msg || JSON.stringify(err)}</div>)
                : error.msg || error.detail || JSON.stringify(error) || 'An error occurred'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Invoices
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and track all your invoices
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              variant="outlined"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              }}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper'
                }
              }}
            />
            <TextField
              select
              label="Vendor"
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Vendors</MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.standard_name && vendor.standard_name.trim() !== '' 
                    ? vendor.standard_name 
                    : vendor.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All Years</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            {/* Sort controls */}
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant={sortBy === 'date' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleSort('date')}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  '&.MuiButton-contained': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Button>
              <Button
                variant={sortBy === 'amount' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleSort('amount')}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  '&.MuiButton-contained': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Button>
            </Box>
            {/* Clear sort button */}
            {(sortBy !== 'created_at' || sortOrder !== 'desc') && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSortBy('created_at');
                  setSortOrder('desc');
                }}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  ml: 1
                }}
              >
                Clear Sort
              </Button>
            )}
            {/* Clear filters button */}
            {(selectedVendorId || selectedYear || searchTerm) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedVendorId('');
                  setSelectedYear('');
                  setSearchTerm('');
                }}
                sx={{ 
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  ml: 1
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/upload')}
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Upload New Invoice
          </Button>
        </Box>


      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="invoices table">
            <TableHead sx={{ backgroundColor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '5%' }}>SN</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '15%' }}>Invoice Number</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '15%' }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: '10%' }}>Uploaded By</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '12%' }}>Total Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '8%' }}>Currency</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '12%' }}>Invoice Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '12%' }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '10%' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', width: '5%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((invoice, index) => (
                <TableRow
                  key={invoice.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onDoubleClick={() => {
                    setSelectedInvoice(invoice);
                    setShowPdfViewer(true);
                  }}
                >
                  <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {startItem + index}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {invoice.invoice_number ? invoice.invoice_number : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {getVendorDisplayName(invoice.vendor_id)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {invoice.owner?.name || invoice.owner?.username || 'N/A'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    ${invoice.total_amount ? parseFloat(invoice.total_amount).toFixed(2) : '0.00'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {invoice.currency || 'N/A'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {getCategoryDisplayName(invoice.category_id)}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={invoice.payment_status}
                      color={getStatusColor(invoice.payment_status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(event) => handleClick(event, invoice)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredInvoices.length === 0 && (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'background.default'
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No invoices found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchTerm ? 'No invoices match your search. Try different keywords.' : 'Upload your first invoice to get started'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/upload')}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Upload Invoice
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Pagination Controls */}
      {totalItems > itemsPerPage && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& ul': { p: 0 }
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetailsClick}>
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        invoice={selectedInvoice}
        onSave={handleEditSave}
        onError={handleEditError}
      />

      {/* PDF Viewer Dialog */}
      {selectedInvoice && (
        <PdfViewerWithDetails
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          invoiceId={selectedInvoice.id}
          type="invoice"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-invoice-dialog-title"
        aria-describedby="delete-invoice-dialog-description"
      >
        <DialogTitle id="delete-invoice-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-invoice-dialog-description">
            Are you sure you want to delete invoice #{selectedInvoice?.id} ({selectedInvoice?.invoice_number || 'no number'}) from {getVendorDisplayName(selectedInvoice?.vendor_id)}?
            This action cannot be undone.
          </DialogContentText>

          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            color="error"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceList;